import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export type ThemeMode = 'light' | 'dark' | 'system'
interface Ctx {
  theme: ThemeMode
  resolved: 'light' | 'dark'
  setTheme: (m: ThemeMode) => void
}

const ThemeContext = createContext<Ctx | null>(null)
const STORAGE_KEY = 'trodo-theme'

const systemTheme = (): 'light' | 'dark' => (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')

function apply(resolved: 'light' | 'dark') {
  document.documentElement.setAttribute('data-theme', resolved)
  document.documentElement.style.colorScheme = resolved
}

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
  } catch {
    return 'system'
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(readStored)
  const [sys, setSys] = useState(systemTheme)
  const resolved = theme === 'system' ? sys : theme

  useEffect(() => apply(resolved), [resolved])
  useEffect(() => {
    const mq = matchMedia('(prefers-color-scheme: dark)')
    const h = () => setSys(systemTheme())
    mq.addEventListener('change', h)
    return () => mq.removeEventListener('change', h)
  }, [])

  const setTheme = useCallback((m: ThemeMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, m)
    } catch {
      /* private mode */
    }
    setThemeState(m)
  }, [])

  const value = useMemo(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
